import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import StatsCard from "../components/StatsCard";
import ServerCard from "../components/ServerCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { LayoutGrid, List, Search, RefreshCw, Filter } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [servers, setServers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, serversRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/servers`, { headers }),
        axios.get(`${API_URL}/api/clients`, { headers })
      ]);

      setStats(statsRes.data);
      
      // Enrich servers with client names
      const clientMap = {};
      clientsRes.data.forEach(c => { clientMap[c.id] = c.name; });
      const enrichedServers = serversRes.data.map(s => ({
        ...s,
        client_name: clientMap[s.client_id] || "Unknown"
      }));
      
      setServers(enrichedServers);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des donnees");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Filter servers
  const filteredServers = servers.filter(server => {
    const matchesSearch = 
      server.hostname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      server.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (server.ip_address && server.ip_address.includes(searchTerm));
    
    const matchesStatus = statusFilter === "all" || server.last_backup_status === statusFilter;
    const matchesClient = clientFilter === "all" || server.client_id === clientFilter;
    
    return matchesSearch && matchesStatus && matchesClient;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Vue d&apos;ensemble des sauvegardes</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="secondary"
          className="bg-slate-800 hover:bg-slate-700 text-white"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatsCard type="clients" value={stats.total_clients} label="Clients" />
          <StatsCard type="servers" value={stats.total_servers} label="Serveurs" />
          <StatsCard type="success" value={stats.status_counts.success} label="Succes" />
          <StatsCard type="warning" value={stats.status_counts.warning} label="Attention" />
          <StatsCard type="error" value={stats.status_counts.error} label="Erreurs" />
          <StatsCard type="stale" value={stats.stale_agents} label="Agents inactifs" />
        </div>
      )}

      {/* Filters */}
      <div className="glass-card rounded-lg p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <Input
              placeholder="Rechercher un serveur, client ou IP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="search-input"
            />
          </div>

          {/* Status filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full lg:w-48 bg-slate-950 border-slate-700 text-white" data-testid="status-filter">
              <Filter className="mr-2 h-4 w-4 text-slate-500" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="success">Succes</SelectItem>
              <SelectItem value="warning">Attention</SelectItem>
              <SelectItem value="error">Erreur</SelectItem>
              <SelectItem value="running">En cours</SelectItem>
              <SelectItem value="unknown">Inconnu</SelectItem>
            </SelectContent>
          </Select>

          {/* Client filter */}
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-full lg:w-48 bg-slate-950 border-slate-700 text-white" data-testid="client-filter">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">Tous les clients</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* View mode toggle */}
          <div className="flex gap-1 bg-slate-950 rounded-md p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("grid")}
              className={viewMode === "grid" ? "bg-slate-700" : ""}
              data-testid="grid-view-btn"
            >
              <LayoutGrid size={18} />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
              className={viewMode === "list" ? "bg-slate-700" : ""}
              data-testid="list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
        </div>
      </div>

      {/* Servers grid/list */}
      {filteredServers.length === 0 ? (
        <div className="glass-card rounded-lg p-12 text-center">
          <p className="text-slate-400">
            {servers.length === 0
              ? "Aucun serveur configure. Ajoutez des clients et serveurs pour commencer."
              : "Aucun serveur ne correspond aux filtres."}
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
          {filteredServers.map((server) => (
            <ServerCard key={server.id} server={server} showClientName />
          ))}
        </div>
      ) : (
        <div className="glass-card rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Serveur</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Client</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Dernier backup</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Job</th>
              </tr>
            </thead>
            <tbody>
              {filteredServers.map((server) => (
                <tr
                  key={server.id}
                  className="border-t border-slate-800 hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => window.location.href = `/servers/${server.id}`}
                  data-testid={`server-row-${server.id}`}
                >
                  <td className="p-4">
                    <div>
                      <p className="font-mono text-white">{server.hostname}</p>
                      {server.ip_address && (
                        <p className="text-xs font-mono text-slate-500">{server.ip_address}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-slate-300">{server.client_name}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      server.last_backup_status === "success" ? "bg-emerald-500/20 text-emerald-400" :
                      server.last_backup_status === "warning" ? "bg-amber-500/20 text-amber-400" :
                      server.last_backup_status === "error" ? "bg-red-500/20 text-red-400" :
                      server.last_backup_status === "running" ? "bg-blue-500/20 text-blue-400" :
                      "bg-slate-500/20 text-slate-400"
                    }`}>
                      {server.last_backup_status}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-slate-400">
                    {server.last_backup_time
                      ? new Date(server.last_backup_time).toLocaleString("fr-FR")
                      : "-"}
                  </td>
                  <td className="p-4 font-mono text-sm text-slate-400">
                    {server.last_backup_job_name || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
